import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Prediction } from '../../types/predictions';

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 8px;
  flex-wrap: wrap;
`;

const ScoreDot = styled.div<{ $status: 'success' | 'miss' | 'updated' | 'pending' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => 
    props.$status === 'success' ? '#22C55E' : // Green for success
    props.$status === 'miss' ? '#EF4444' :    // Red for miss
    props.$status === 'updated' ? '#8B5CF6' : // Purple for updated
    '#FF9800'                                 // Orange for pending
  };
  opacity: 0.9;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
`;

interface InfluencerScoreDotsProps {
  influencerName: string;
  allPredictions: Prediction[];
  maxDots?: number;
}

const InfluencerScoreDots: React.FC<InfluencerScoreDotsProps> = ({ 
  influencerName, 
  allPredictions, 
  maxDots = 10 
}) => {
  
  // Get all predictions for this influencer, sorted chronologically
  const influencerHistory = useMemo(() => {
    const influencerPredictions = allPredictions
      .filter(prediction => {
        // More robust name matching - handle potential undefined/null cases
        const predictionName = prediction.influencer?.name?.trim();
        const searchName = influencerName?.trim();
        return predictionName && searchName && predictionName === searchName;
      })
      .sort((a, b) => {
        // Sort by prediction_made date first, then created_at, then end_date
        const aDate = new Date(a.prediction_made || a.created_at || a.end_date || 0);
        const bDate = new Date(b.prediction_made || b.created_at || b.end_date || 0);
        return aDate.getTime() - bDate.getTime();
      })
      .slice(-maxDots); // Keep only the most recent ones

    return influencerPredictions;
  }, [influencerName, allPredictions, maxDots]);

  // Don't show anything if no predictions
  if (influencerHistory.length === 0) {
    return null;
  }

  return (
    <ScoreContainer>
      {influencerHistory.map((prediction, index) => (
        <ScoreDot 
          key={`${prediction.id}-${index}`}
          $status={(prediction.success_status || 'pending') as 'success' | 'miss' | 'updated' | 'pending'}
          title={`${(prediction.success_status || 'pending').toUpperCase()}: ${prediction.prediction_made ? new Date(prediction.prediction_made).toLocaleDateString() : 'Date unknown'}`}
        />
      ))}
    </ScoreContainer>
  );
};

export default InfluencerScoreDots;